import { Prisma } from "@/generated/prisma";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function normalizeUrl(u: string): string {
  return u.replace(/\/+$/, "");
}

/**
 * Convert unknown -> Prisma.InputJsonValue (strict JSON-ish).
 * Avoids TS error: Record<string, unknown> not assignable to InputJsonValue.
 */
export function toInputJsonValue(value: unknown): Prisma.InputJsonValue {
  if (value === null) return {};
  if (typeof value === "string") return value;
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value;

  if (typeof value === "undefined") return {};

  if (Array.isArray(value)) {
    return value.map((v) => toInputJsonValue(v));
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const out: Record<string, Prisma.InputJsonValue> = {};
    for (const [k, v] of Object.entries(obj)) out[k] = toInputJsonValue(v);
    return out;
  }

  return String(value as string);
}

export function toBigInt(value: unknown): bigint | null {
  if (value === null || typeof value === "undefined") return null;
  if (typeof value === "bigint") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value < 0) return null;
    return BigInt(Math.floor(value));
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    // allow "123" only
    if (!/^\d+$/.test(trimmed)) return null;
    return BigInt(trimmed);
  }
  return null;
}
export function toRecordOrNull(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== "object") return null;
  if (Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export function toDayKey(d: Date): string {
  return d.toISOString().slice(0, 10); // UTC bucket
}

export function getAppUrl(): string {
  return (
    process.env.APP_URL ||
    process.env.NEXT_PUBLIC_APP_URL ||
    "http://localhost:3000"
  );
}